// Ausgangsrechnung (Belegart 'invoice', RE-) und Rechnungskorrektur (Belegart 'credit_note', GS-).
//
//   Rechnung            aus einem oder mehreren zugestellten Lieferscheinen EINES Kunden (Sammelrechnung), ganz oder in
//                       Teilmengen. Rücknahmen dieser Lieferungen, die noch nicht gutgeschrieben sind, zieht die
//                       Rechnung als eigene Zeile ab – berechnet wird nie mehr als geliefert abzüglich zurückgenommen.
//   Rechnungskorrektur  aus einer Rücknahme (nach der Rechnung) oder als freie Korrektur mit Bezug auf die Rechnung.
//   Rechnungslauf       abrechenbare Lieferungen je Kunde mit der Vorschau jeder Rechnung, gebucht in EINEM Schritt.
// Der Lieferschein folgt der Berechnung (Zustand „Berechnet“) über den Belegkern und das Modul Verkauf.
import { defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CREDIT_NOTE_TYPE, DELIVERY_TYPE, INVOICE_TYPE, RETURN_TYPE, zAmountCents, zDay, zEInvoiceCheck, zInvoiceEInvoice, zLocationRef, zPartnerRef, zReason } from './common'

export const INVOICE_PERMISSIONS = {
  read: 'finance.invoice.read',
  write: 'finance.invoice.write',
  post: 'finance.invoice.write',
  reverse: 'finance.invoice.write',
} as const

export const INVOICE_SOURCE_TYPES = [DELIVERY_TYPE, RETURN_TYPE] as const
export const CREDIT_NOTE_SOURCE_TYPES = [RETURN_TYPE, INVOICE_TYPE] as const

export const invoice = defineDocumentContracts({
  module: 'finance',
  entity: 'invoice',
  documentType: INVOICE_TYPE,
  title: 'Rechnung',
  titlePlural: 'Rechnungen',
  permissions: INVOICE_PERMISSIONS,
  derivesFrom: INVOICE_SOURCE_TYPES,
  keywords: ['Ausgangsrechnung', 'Kundenrechnung', 'Sammelrechnung', 'fakturieren', 'berechnen', 'RE'],
})

export const invoiceGet = invoice.get
export const invoiceList = invoice.list
export const invoiceSaveDraft = invoice.saveDraft
export const invoicePost = invoice.post
export const invoiceReverse = invoice.reverse
export const invoiceTransition = invoice.transition
export const invoicePrint = invoice.print
export const invoiceDiscard = invoice.discard
/** aus Lieferscheinen; noch nicht gutgeschriebene Rücknahmen der Lieferungen werden von selbst abgezogen */
export const invoiceDerive = invoice.derive

export const creditNote = defineDocumentContracts({
  module: 'finance',
  entity: 'creditNote',
  documentType: CREDIT_NOTE_TYPE,
  title: 'Rechnungskorrektur',
  titlePlural: 'Rechnungskorrekturen',
  permissions: INVOICE_PERMISSIONS,
  derivesFrom: CREDIT_NOTE_SOURCE_TYPES,
  keywords: ['Gutschrift', 'Rechnungskorrektur', 'Korrekturrechnung', 'Retoure gutschreiben', 'GS'],
})

export const creditNoteGet = creditNote.get
export const creditNoteList = creditNote.list
export const creditNoteSaveDraft = creditNote.saveDraft
export const creditNotePost = creditNote.post
export const creditNoteReverse = creditNote.reverse
export const creditNoteTransition = creditNote.transition
export const creditNotePrint = creditNote.print
export const creditNoteDiscard = creditNote.discard
/** aus einer Rücknahme (sales_return) oder aus Zeilen einer Rechnung */
export const creditNoteDerive = creditNote.derive

export const creditNoteCorrect = defineContract({
  name: 'finance.creditNote.correct',
  title: 'Freie Rechnungskorrektur anlegen',
  description: 'Legt eine Rechnungskorrektur als Entwurf an, die sich auf eine Rechnung bezieht – für Preisnachlässe und Berichtigungen ohne Warenrückgabe.',
  llm: {
    description:
      'Legt eine Rechnungskorrektur als ENTWURF an, die eindeutig auf eine festgeschriebene Rechnung verweist: je Zeile ' +
      'eine Bezeichnung, der Betrag in Cent (positiv; in der Preisbasis der Rechnung – netto bei Netto-Rechnungen) und der ' +
      'Steuerschlüssel. Verwenden für Preisnachlässe, Kulanz oder berichtigte Preise OHNE Warenrückgabe. Kommt Ware ' +
      'zurück: erst die Rücknahme im Verkauf buchen, dann finance.creditNote.derive mit der Rücknahme. Ganz falsche ' +
      'Rechnung: finance.invoice.reverse. Gebucht ist nichts, bis finance.creditNote.post den Entwurf festschreibt.',
    keywords: ['Rechnungskorrektur', 'Gutschrift', 'Preisnachlass', 'Kulanz', 'Rechnung berichtigen'],
  },
  input: z.strictObject({
    invoiceId: zId,
    reason: zReason,
    lines: z
      .array(
        z.strictObject({
          description: z.string({ error: 'Bitte eine Bezeichnung eingeben.' }).trim().min(2, { error: 'Bitte eine Bezeichnung eingeben.' }).max(200),
          /** Betrag der Korrektur in Cent, positiv, in der Preisbasis der Rechnung */
          amountCents: zAmountCents,
          taxCodeId: zId,
        }),
      )
      .min(1, { error: 'Bitte mindestens eine Zeile angeben.' })
      .max(50),
  }),
  output: zDocumentView,
  permission: INVOICE_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': 'costs.read' },
})

const zRunDelivery = z.object({
  id: zId,
  number: z.string().nullable(),
  /** Tag der Lieferung (Leistungsdatum) */
  serviceDate: z.string(),
  orderNumber: z.string().nullable(),
  /** berechnet wird der noch offene Teil */
  partial: z.boolean(),
  netCents: z.int(),
  grossCents: z.int(),
})

const zRunLine = z.object({
  kind: z.enum(['item', 'fee', 'discount', 'deposit', 'return', 'text']),
  description: z.string(),
  /** Menge als Text mit Gebinde: „16 Ka“ */
  qtyText: z.string(),
  unitPriceCents: z.int().nullable(),
  /** Zeilenbetrag in der Preisbasis der Rechnung (Abzüge negativ) */
  amountCents: z.int(),
  taxRateBp: z.int().nullable(),
  /** Belegnummer des Lieferscheins bzw. der Rücknahme */
  source: z.string().nullable(),
})

export const zRunGroup = z.object({
  /** Kunde, Standort und Preisbasis – je Gruppe entsteht EINE Rechnung */
  key: z.string(),
  partner: zPartnerRef,
  location: zLocationRef,
  priceMode: z.enum(['gross', 'net']),
  deliveries: z.array(zRunDelivery),
  /** Rücknahmen dieser Lieferungen, die die Rechnung abzieht */
  returns: z.array(z.object({ id: zId, number: z.string().nullable(), documentDate: z.string(), grossCents: z.int() })),
  lines: z.array(zRunLine),
  taxes: z.array(z.object({ rateBp: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int() })),
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
  /** Pfandsaldo brutto, in grossCents enthalten */
  depositCents: z.int(),
  serviceFrom: z.string(),
  serviceTo: z.string(),
  paymentTerms: z.string().nullable(),
  dueDate: z.string().nullable(),
  discountText: z.string().nullable(),
  /**
   * Hinweis zur E-Rechnung als ein Satz (entsteht eine XRechnung? Funktion aus?) – für die heutige Oberfläche; die
   * Einzelheiten stehen in `eInvoice`.
   */
  eInvoiceNotice: z.string().nullable(),
  /**
   * Bestellnummer des Kunden, unter der diese Rechnung läuft (alle Lieferscheine der Gruppe tragen sie). Kunden mit
   * „Bestellnummer Pflicht“ bekommen eine Rechnung je Bestellnummer.
   */
  orderRef: z.string().nullable().optional(),
  /**
   * Vorprüfung der E-Rechnung (einvoice.outbound.preflightDraft): ob beim Buchen eine XRechnung entsteht und was dafür
   * fehlt. Ein Befund „error“ setzt `billable` auf false. null: nicht geprüft (Summen nicht berechenbar).
   */
  eInvoice: zEInvoiceCheck.nullable().optional(),
  /** Sätze, die vor dem Buchen zu lesen sind (gesperrter Kunde, fehlende Anschrift, E-Rechnung) */
  warnings: z.array(z.string()),
  /** false: Diese Gruppe lässt sich so nicht berechnen – der Grund steht in warnings */
  billable: z.boolean(),
})
export type InvoiceRunGroup = z.output<typeof zRunGroup>

export const invoiceRunPreview = defineContract({
  name: 'finance.invoiceRun.preview',
  title: 'Rechnungslauf vorbereiten',
  description: 'Abrechenbare Lieferungen je Kunde mit der Vorschau jeder Rechnung: Positionen, Steuer, Summe, Zahlungsziel.',
  llm: {
    description:
      'Zeigt, welche Rechnungen ein Rechnungslauf erzeugen würde: zugestellte, noch nicht (vollständig) berechnete ' +
      'Lieferscheine, gruppiert je Kunde, Standort und Preisbasis – je Gruppe eine Sammelrechnung mit Positionen, ' +
      'abgezogenen Rücknahmen, Steuer je Satz und Summe in Cent. Verändert nichts. ' +
      'Verwenden für „Was ist abzurechnen?“ und vor finance.invoiceRun.post. Filter: Kunde, Standort, zugestellt bis. ' +
      'Schon geschriebene Rechnungen: finance.invoice.list.',
    keywords: ['Rechnungslauf', 'abrechnen', 'Sammelrechnung', 'Rechnungen schreiben', 'noch nicht berechnet', 'fakturieren'],
  },
  input: z.strictObject({
    partnerId: zId.optional(),
    locationId: zId.optional(),
    /** nur Lieferungen bis zu diesem Tag (Sammelrechnung für einen Zeitraum) */
    until: zDay.optional(),
    /** Rechnungsdatum; Standard: heute */
    documentDate: zDay.optional(),
    /**
     * nur fällige Sammelrechnungskunden (docs/belege-nach-zustellung §4): Kunden mit wöchentlicher, 14-täglicher oder
     * monatlicher Sammelrechnung, je mit den Lieferungen bis zum Stichtag ihres letzten fälligen Laufs
     */
    dueCollective: z.boolean().optional(),
  }),
  output: z.object({
    documentDate: z.string(),
    groups: z.array(zRunGroup),
    totals: z.object({ invoiceCount: z.int(), deliveryCount: z.int(), netCents: z.int(), taxCents: z.int(), grossCents: z.int() }),
  }),
  permission: INVOICE_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

export const invoiceRunPost = defineContract({
  name: 'finance.invoiceRun.post',
  title: 'Rechnungslauf buchen',
  description: 'Schreibt die gewählten Rechnungen in einem Schritt fest: je Kunde eine Rechnung über seine Lieferungen, mit lückenloser Nummer und offenem Posten.',
  llm: {
    description:
      'Bucht einen Rechnungslauf: Für jede Gruppe aus finance.invoiceRun.preview (Kunde, Standort, Lieferscheine) ' +
      'entsteht EINE festgeschriebene Rechnung mit lückenloser Nummer und offenem Posten; die Lieferscheine gelten ' +
      'danach als berechnet. `expectedGrossCents` ist die Summe aus der Vorschau – weicht sie ab, wird nichts gebucht. ' +
      'Vor der ersten Buchung prüft der Lauf jede Rechnung auf die Angaben für die E-Rechnung; fehlt einem Kunden etwas, ' +
      'bricht er mit der Liste aller betroffenen Kunden ab. Gruppen mit billable = false aus der Vorschau nicht senden. ' +
      'Alle Rechnungen entstehen gemeinsam oder gar nicht. Nicht umkehrbar außer durch Storno jeder einzelnen Rechnung ' +
      '(finance.invoice.reverse). Für eine einzelne Rechnung mit Teilmengen: finance.invoice.derive.',
    keywords: ['Rechnungslauf buchen', 'Rechnungen schreiben', 'Sammelrechnung', 'fakturieren', 'abrechnen'],
  },
  input: z.strictObject({
    /** Rechnungsdatum aller Rechnungen; Standard: heute */
    documentDate: zDay.optional(),
    groups: z
      .array(
        z.strictObject({
          partnerId: zId,
          locationId: zId,
          deliveryIds: z.array(zId).min(1, { error: 'Bitte mindestens einen Lieferschein wählen.' }).max(100),
          /** Rechnungssumme brutto aus der Vorschau */
          expectedGrossCents: z.int({ error: 'Die Summe aus der Vorschau fehlt.' }),
        }),
      )
      .min(1, { error: 'Bitte mindestens eine Rechnung wählen.' })
      .max(100),
  }),
  output: z.object({
    invoices: z.array(
      z.object({
        id: zId,
        number: z.string(),
        partner: zPartnerRef,
        netCents: z.int(),
        grossCents: z.int(),
        dueDate: z.string().nullable(),
        deliveryCount: z.int(),
        /** beim Festschreiben ist eine E-Rechnung (XRechnung) entstanden */
        eInvoice: z.boolean().optional(),
      }),
    ),
    count: z.int(),
    grossCents: z.int(),
  }),
  permission: INVOICE_PERMISSIONS.write,
  risk: 'financial',
  scope: (input) => [...new Set(input.groups.map((group) => group.locationId))],
  limits: { maxItems: 100, itemCount: (input) => input.groups.length },
})

export const invoiceStatus = defineContract({
  name: 'finance.invoice.status',
  title: 'Zahl- und Mahnstand einer Rechnung anzeigen',
  description: 'Zahlungen, Skonto, Verrechnungen, Mahnstufe und Korrekturen zu einer Rechnung oder Rechnungskorrektur.',
  llm: {
    description:
      'Liefert zu EINER Rechnung oder Rechnungskorrektur den Zahlstand: offener Betrag, Fälligkeit, Skontofrist, jede ' +
      'zugeordnete Zahlung mit Datum, Zahlart, Betrag und Skonto, Verrechnungen mit Rechnungskorrekturen, Mahnstufe und ' +
      'Mahnsperre sowie den Stand der E-Rechnung (ausgestellt mit Datei und Übermittlungen, entsteht beim Festschreiben, ' +
      'es fehlen Angaben, keine nötig) und eine laufende Berichtigung. Verwenden für „Ist RE-… bezahlt?“ und „Hat RE-… ' +
      'eine E-Rechnung?“. Den Beleg selbst liefert finance.invoice.get; alle offenen Posten eines Kunden finance.openItem.list.',
    keywords: ['bezahlt', 'Zahlstand', 'Zahlungseingang zur Rechnung', 'Mahnstufe', 'offen'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    documentId: zId,
    number: z.string().nullable(),
    posted: z.boolean(),
    openItem: z
      .object({
        id: zId,
        amountCents: z.int(),
        openCents: z.int(),
        status: z.string(),
        dueDate: z.string().nullable(),
        daysOverdue: z.int(),
        discountUntil: z.string().nullable(),
        discountBp: z.int(),
        /** Skontobetrag, solange die Frist läuft – sonst 0 */
        discountCents: z.int(),
        dunningLevel: z.int(),
        dunningBlocked: z.boolean(),
        dunningBlockReason: z.string().nullable(),
        lastDunnedAt: z.string().nullable(),
      })
      .nullable(),
    postings: z.array(
      z.object({
        id: z.string(),
        /** payment | discount | offset | write_off | reversal */
        kind: z.string(),
        label: z.string(),
        date: z.string(),
        /** mindert den offenen Betrag (Rücknahmen positiv) */
        amountCents: z.int(),
        paymentId: zId.nullable(),
        reference: z.string().nullable(),
      }),
    ),
    /** Rücknahmen zu den Lieferungen dieser Rechnung, die noch nicht gutgeschrieben sind – daraus entsteht die Rechnungskorrektur */
    pendingReturns: z.array(z.object({ id: zId, number: z.string().nullable(), documentDate: z.string(), grossCents: z.int() })),
    /** Rechnung, auf die sich eine Rechnungskorrektur bezieht – bzw. die eine berichtigte Rechnung ersetzt */
    corrects: z.object({ id: zId, number: z.string().nullable(), documentDate: z.string(), grossCents: z.int().optional() }).nullable(),
    /** Berichtigte Rechnung zu dieser Rechnung (Ablauf „Rechnung berichtigen“): offener Entwurf oder festgeschrieben */
    rectification: z.object({ id: zId, number: z.string().nullable(), posted: z.boolean() }).nullable(),
    /** Stand der E-Rechnung als ein Satz (null: nichts zu sagen) – für die heutige Oberfläche; Einzelheiten in `eInvoice` */
    eInvoiceNotice: z.string().nullable(),
    eInvoice: zInvoiceEInvoice,
  }),
  permission: INVOICE_PERMISSIONS.read,
  risk: 'read',
  scope: 'record',
})
